import Excel from 'exceljs';
import isNil from 'lodash/isNil';
import toString from 'lodash/toString';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import isValid from 'date-fns/isValid';

import { TExcelDataRow } from 'models/excel';
import { PRODUCT_DATE_FIELD } from 'components/constants-ts';
import { COLUMN_DETAILS, EXCEL_ERROR_VALUES, VALID_DATE_FORMATS } from '../../constants';
import { loadExcelFile } from './loadExcelFile';
import { findExcelHeaderRowIndex, getShortTitle } from './parseExcelDataToProduct';
import { isPhantomRow, isSparseRow } from './phantomRows';

export type ReadExcelFileOptions = {
  /**
   * 1-based Excel row number to use as the header. When supplied, pre-header
   * rows are dropped before returning so downstream `parseExcelDataToProduct`
   * lands the data on row 0.
   *
   * If the row number cannot be located in the non-hidden row set
   * (mistyped override, hidden row picked), the override is silently ignored
   * and auto-detection proceeds — wrong-override should degrade to current
   * behaviour, not produce an empty result.
   */
  headerRow?: number;
  /**
   * Drop vendor-template scaffolding rows. Covers three classes:
   *   1. `Läs mer` / `Read more` annotation rows
   *   2. rows whose first non-empty cell carries `(exempel)` / `(example)`
   *   3. sparse rows (< 2 non-empty cells) — e.g. a status formula dragged
   *      down a single column to ~row 500, leaving rows of just "0"
   *
   * Default `false` to preserve behaviour for standard Foodla templates,
   * where a producer's own free-text starting with these tokens, or a
   * legitimately sparse row, must NOT be silently dropped — the producer
   * should see it in the review grid and fix it.
   *
   * Set `true` only when the caller has signal that the file is a vendor
   * template (e.g. multi-sheet, picker visible, explicit headerRow override
   * below row 1). The header row is never dropped regardless.
   */
  dropPhantomRows?: boolean;
};

const parseDateString = (valueParam: string | number) => {
  let validDate: Date | undefined;
  let value = '';
  if (typeof valueParam === 'number') {
    const date = new Date(0, 0, valueParam - 1, 0, -new Date(0).getTimezoneOffset(), 0);
    value = format(date, 'yyyy-MM-dd');
  } else {
    value = valueParam;
  }

  VALID_DATE_FORMATS.forEach(dateFormat => {
    if (validDate) return;

    const parsedDate = parse(value, dateFormat, new Date());
    if (!isValid(parsedDate)) return;

    validDate = parsedDate;
  });

  return validDate;
};

const removeSpecialCharactors = (value: string) => {
  const specialCharactorCodes = [8203, 8204, 8205, 8206, 8207];
  if (typeof value !== 'string') return value;

  return value
    .split('')
    .map(character => (specialCharactorCodes.includes(character.charCodeAt(0)) ? '' : character))
    .join('');
};

export const readExcelFile = async (
  file: File,
  sheetIndex = 0,
  options?: ReadExcelFileOptions
): Promise<TExcelDataRow[]> => {
  const workbook = await loadExcelFile(file);

  if (!workbook) return [];

  const workSheet = workbook.worksheets[sheetIndex];
  if (!workSheet) return [];
  const excelData: TExcelDataRow[] = [];

  // Author-hidden columns are template internals — Apotea hides an OK-status
  // `=IF(...)` formula column and spacer columns to the left of the real
  // headers. Skip them the same way we skip hidden rows (below) and hidden
  // sheets (in the analyzer); otherwise they surface in FieldsMapping as
  // blank, unmappable columns. Verified that no standard Foodla template hides
  // any column, so this is a no-op for them. Columns are compacted (removed),
  // so downstream sees only visible columns — applied uniformly to the header
  // and every data row, keeping them aligned.
  const hiddenColumns = new Set<number>();
  const columnCount = workSheet.columnCount || 0;
  for (let c = 1; c <= columnCount; c++) {
    if (workSheet.getColumn(c)?.hidden) hiddenColumns.add(c);
  }

  const productDateHeaders = COLUMN_DETAILS.filter(({ field }) => PRODUCT_DATE_FIELD.includes(field)).reduce(
    (all, { title, searchTitleList }) => [...all, title, ...(searchTitleList || [])],
    [] as string[]
  );

  // Track Excel row numbers alongside cell values so a user-supplied
  // `headerRow` (Excel-numbered) can be mapped back to the position in this
  // filtered array (which only contains non-hidden rows).
  const rowValues: TExcelDataRow[] = [];
  const excelRowNumbers: number[] = [];
  workSheet.eachRow((row, rowNumber) => {
    if (row.hidden) return;
    rowValues.push(row.values as unknown as TExcelDataRow);
    excelRowNumbers.push(rowNumber);
  });

  // If an override was provided AND the row exists in the non-hidden set,
  // use its position; otherwise fall back to content-based detection.
  let headerRowIndex = findExcelHeaderRowIndex(rowValues);
  if (options?.headerRow != null) {
    const overrideIndex = excelRowNumbers.indexOf(options.headerRow);
    if (overrideIndex >= 0) headerRowIndex = overrideIndex;
  }

  workSheet.eachRow((row, rowNumber) => {
    if (row.hidden) return;
    const headers = excelData[headerRowIndex] || [];

    const _cells = (row as any)._cells;
    const values = row.values as unknown as Excel.CellValue[];
    let rowData: TExcelDataRow = [];
    for (let colNumber = 1; colNumber < values?.length; colNumber++) {
      if (hiddenColumns.has(colNumber)) continue; // skip author-hidden columns

      const value = values[colNumber] as any;

      // `headers` (the parsed header row) is compacted the same way, so index
      // it by the compacted position (`rowData.length`), not the raw column.
      const excelTitle = getShortTitle(String(headers[rowData.length] || ''));
      const numFmt = toString(_cells[colNumber - 1]?.numFmt);
      const isPercent = rowNumber !== 1 && numFmt.endsWith('%');
      const isDate = rowNumber !== 1 && productDateHeaders.includes(excelTitle);
      const isLink = value?.hyperlink;

      if (isNil(value)) {
        rowData.push(null);
      } else if (isLink) {
        rowData.push(value.text || null);
      } else if (isDate || value instanceof Date) {
        rowData.push(parseDateString(value) || value);
      } else if (isPercent) {
        if (typeof value === 'number') {
          rowData.push(`${Number(value) * 100}%`);
        } else {
          rowData.push(value);
        }
      } else if (typeof value !== 'object') {
        rowData.push(removeSpecialCharactors(value as string));
      } else if (!isNil(value.result)) {
        rowData.push(value.result);
      } else if (value.richText?.length) {
        const newValue = value.richText.map(({ text }: { text: string }) => text).join('');
        rowData.push(removeSpecialCharactors(newValue as string));
      } else {
        console.error(`!!!Error Unknown cell value ${headers[rowData.length]} (${typeof value}): ${value}`);
        rowData.push(null);
      }
    }
    rowData = rowData.map(data => {
      if (typeof data === 'string') return data.trim();
      return data;
    })
    excelData.push(rowData);
  });

  const result: TExcelDataRow[] = [];
  // Parallel to `result`. Lets us locate the user-supplied `headerRow`
  // (Excel-numbered) in the post-filter result array for slicing.
  const resultRowNumbers: number[] = [];
  let phantomDroppedCount = 0;
  let sparseDroppedCount = 0;

  for (let rowIndex = 0; rowIndex < excelData.length; rowIndex++) {
    // remove empty row
    if (!excelData[rowIndex]?.length) continue;
    const row = [...excelData[rowIndex]];

    // fill null to 'error' cell
    for (let colIndex = 0; colIndex < row.length; colIndex++) {
      if (EXCEL_ERROR_VALUES.includes(String(row[colIndex]))) {
        row[colIndex] = null;
      }
    }

    // check valid row
    if (!row.some(cell => cell !== null && String(cell).trim() !== '')) continue;

    // Phantom / sparse-row filter is opt-in via `dropPhantomRows`. Standard
    // Foodla templates leave it off (a real producer row that starts with
    // `Läs mer på vår hemsida...`, or a sparsely-filled row, must survive and
    // be visible in the review grid). Vendor-template uploads (Apotea etc.)
    // opt in via the SheetPicker / headerRow path. The header row is never
    // dropped — it's the column legend the rest of the parse depends on.
    // Skipping invalid-EAN / mandatory-missing rows belongs to the review
    // screen's validation layer regardless.
    if (options?.dropPhantomRows && rowIndex !== headerRowIndex) {
      const cellStrings = row.map(cell => (cell == null ? '' : String(cell)));
      if (isPhantomRow(cellStrings)) {
        phantomDroppedCount += 1;
        continue;
      }
      if (isSparseRow(cellStrings)) {
        sparseDroppedCount += 1;
        continue;
      }
    }

    result.push(row);
    resultRowNumbers.push(excelRowNumbers[rowIndex]);
  }

  if (phantomDroppedCount > 0 || sparseDroppedCount > 0) {
    // Visible to devs in browser console; future PR can surface this in
    // the review UI ("N rader hoppades över som mall-text").
    // eslint-disable-next-line no-console
    console.info(
      `[readExcelFile] dropped ${phantomDroppedCount} phantom (annotation / example) + ${sparseDroppedCount} sparse scaffold row(s) (vendor-template cleanup)`,
    );
  }

  // Always slice the result so the effective header lands at index 0. This
  // gives the caller a single, predictable contract: "row 0 is the header".
  //
  // Effective header is determined by, in order:
  //   1. `options.headerRow` if it survived empty / phantom filtering
  //   2. content-based auto-detect via `findExcelHeaderRowIndex`
  //
  // An invalid override (mistyped row number, hidden row, phantom-filtered
  // row) silently degrades to auto-detect rather than producing an empty or
  // header-less result. This guarantee is what makes
  // `setExcelHeaderRowIndex(0)` safe to pin unconditionally in `DataImport`
  // — without it, an invalid override would pin a wrong row.
  let effectiveHeaderIndex = -1;
  if (options?.headerRow != null) {
    effectiveHeaderIndex = resultRowNumbers.indexOf(options.headerRow);
  }
  if (effectiveHeaderIndex < 0) {
    effectiveHeaderIndex = findExcelHeaderRowIndex(result);
  }
  if (effectiveHeaderIndex > 0) return result.slice(effectiveHeaderIndex);
  return result;
};
